import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Upload } from "lucide-react";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { isLogoOk } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Wordmark } from "@/components/site/Logo";
import { btnPrimary, btnSecondary, ConfirmDelete, FieldError, Panel } from "@/components/admin/AdminLayout";

const LOGO_ERR = "Please upload a JPG, PNG, WEBP or HEIC image under 10 MB";

export const LogoManager = ({ logo, onChange }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    if (!isLogoOk(file)) return setError(LOGO_ERR);
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/logo", form);
      onChange(data.logo);
      toast.success("Logo updated");
    } catch (err) {
      setError(err.response?.status === 400 ? LOGO_ERR : errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      const { data } = await api.delete("/admin/logo");
      onChange(data.logo);
      toast.success("Logo removed");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Panel title="Logo" testId="logo-panel">
      <div className="grid min-h-[140px] place-items-center rounded-md border border-zinc-800 bg-[#121214] p-4" data-testid="logo-preview">
        {logo?.url ? (
          <img src={mediaUrl(logo.url)} alt="Current logo" className="max-h-48 w-auto max-w-full object-contain" data-testid="logo-preview-image" />
        ) : (
          <Wordmark testId="logo-preview-wordmark" />
        )}
      </div>
      <FieldError msg={error} testId="logo-error" />
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <input ref={input} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="hidden" onChange={pick} data-testid="logo-file-input" />
        <Button type="button" onClick={() => input.current?.click()} disabled={busy} className={btnPrimary} data-testid="logo-upload-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload new logo
        </Button>
        {logo?.url && (
          <ConfirmDelete
            description="The site will show the text “JIMMYZ RESTOZ” instead."
            confirmLabel="Yes, remove logo"
            onConfirm={remove}
            trigger={<Button type="button" className={btnSecondary} data-testid="logo-remove-button"><Trash2 className="h-4 w-4" /> Remove logo</Button>}
          />
        )}
      </div>
      <p className="mt-2 text-sm text-zinc-500">JPG, PNG, WEBP or HEIC, up to 10 MB.</p>
    </Panel>
  );
};
